// @flow
import zentakeApi from 'api'
import {
  downloadPacketResponseFile,
  downloadResponseFile,
} from 'api/fileServerApi'
import { downloadPdfDocument } from 'api/pdfFillerApi'
import Loading from 'components/Loading'
import ModalFormUnavailable from 'components/Modal/ModalFormUnavailable'
import useQuery from 'hooks/useQuery'
import {
  getPublicResponse,
  getResponse,
} from 'queries/responses/services/loadResponses'
import * as React from 'react'
import { useParams, withRouter } from 'react-router-dom'
import type { PacketResponse } from 'types/packet-response'
import type { Response } from 'types/response'
import { ZentakeContext } from 'utils/ZentakeContext'

import { ModalSuccess } from '@/components/Modal/ModalSuccess'

type Props = {
  location: {
    search: string,
  },
  match: {
    params: {
      responseId: string,
    },
  },
  history: {
    go: (number) => void,
  },
}

const ResponseCustomerSubmitted = ({
  history,
  location,
  match,
}: Props): React.Node => {
  const { responseSubmitted, setResponseSubmitted } =
    React.useContext(ZentakeContext)

  const [responseObject, setResponseObject] = React.useState<void | {
    response?: ?Response,
    packetResponse?: ?PacketResponse,
  }>(undefined)

  const [loading, setLoading] = React.useState(true)
  const [submittedNow, setSubmittedNow] = React.useState(false)

  const { responseId } = useParams()
  const query = useQuery()
  const searchParams = new URLSearchParams(location.search)
  const isKiosk = searchParams.get('isKiosk') === 'true'

  const responseData =
    responseObject?.response || responseObject?.packetResponse

  const onRedirect = React.useCallback(() => {
    if (isKiosk) return

    const response = responseObject?.response || responseObject?.packetResponse
    if (response?.redirect?.redirect_url) {
      window.location.replace(
        `/redirect/?url=${encodeURIComponent(response?.redirect?.redirect_url)}`
      )
      return null
    }
  }, [responseObject, isKiosk])

  React.useEffect(() => {
    const response = responseObject?.response || responseObject?.packetResponse
    if (response?.is_submitted) {
      return onRedirect()
    }
  }, [onRedirect, responseObject?.packetResponse, responseObject?.response])

  const isPacket = React.useMemo(
    () => Boolean(responseObject?.packetResponse),
    [responseObject]
  )

  const downloadable = React.useMemo(() => {
    if (isPacket) {
      return responseObject?.packetResponse?.responses?.some(
        (r) => r?.form?.downloadable || r?.document?.downloadable
      )
    }
    return (
      responseObject?.response?.form?.downloadable ||
      responseObject?.response?.document?.downloadable
    )
  }, [isPacket, responseObject])

  const onDownloadResponse = React.useCallback(async () => {
    if (!responseObject || !downloadable) {
      return
    }

    setLoading(true)

    const { responseId: messageCode } = match.params

    try {
      if (!isPacket) {
        if (!responseObject?.response?.document) {
          await downloadResponseFile(
            responseObject?.response?.id,
            {
              customerFirstName:
                responseObject?.response?.customer?.first_name ?? '',
              customerLastName:
                responseObject?.response?.customer?.last_name ?? '',
              messageCode,
              publicMode: true,
            },
            { useFakeToken: true }
          )
        } else {
          await downloadPdfDocument(
            responseObject?.response?.id,
            false,
            messageCode
          )
        }
      } else {
        await downloadPacketResponseFile(
          responseObject?.packetResponse?.id,
          {
            format: 'pdf',
            customerFirstName:
              responseObject?.packetResponse?.customer?.first_name ?? '',
            customerLastName:
              responseObject?.packetResponse?.customer?.last_name ?? '',
            onlyDownloadable: true,
            forcePDF: true,
            messageCode,
            publicMode: true,
          },
          { useFakeToken: true }
        )
      }
    } catch (error) {
    } finally {
      setLoading(false)
    }
  }, [responseObject, downloadable, match.params, isPacket])

  React.useEffect(() => {
    const fetchData = async () => {
      try {
        if (query.get('response_type') !== 'packet') {
          let formResponse: Response

          if (isKiosk) {
            formResponse = await getResponse(responseId)
          } else {
            formResponse = await getPublicResponse(responseId)
          }

          if (formResponse?.id) {
            setResponseObject({ response: formResponse })
          }
        } else {
          let packetResponse: PacketResponse
          if (isKiosk) {
            packetResponse = await zentakeApi.getResponsePacket(responseId)
          } else {
            packetResponse =
              await zentakeApi.getPublicResponsePacket(responseId)
          }
          if (packetResponse?.id) {
            setResponseObject({
              packetResponse,
            })
          }
        }
      } catch (error) {
        if (error?.detail?.detail === 'Response is already submitted.') {
          // $FlowFixMe
          setResponseObject({ response: { is_submitted: true } })
        }
      } finally {
        setLoading(false)
      }
    }

    setSubmittedNow(responseSubmitted)
    setResponseSubmitted(false)
    fetchData()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const hasRedirectUrl = responseData?.redirect?.redirect_url

  if (loading || (!isKiosk && hasRedirectUrl)) {
    return <Loading />
  }

  if (!responseObject) {
    return (
      <ModalFormUnavailable
        hideButton
        isVisible
        description="Sorry, this form is not available anymore"
        title="Form not available"
      />
    )
  }

  return (
    <ModalSuccess.Custom
      isVisible
      buttonTitle="Download a copy"
      callback={() => history.go(-2)}
      callbackDelay={5000}
      data={responseData?.redirect?.custom_complete_page}
      hasCallback={isKiosk}
      hideButton={!(submittedNow && downloadable && !isKiosk)}
      onClose={onDownloadResponse}
    />
  )
}

const EnhancedResponseCustomerSubmitted: React.ComponentType<Props> =
  withRouter(ResponseCustomerSubmitted)

export default EnhancedResponseCustomerSubmitted
